/**
 * Dialog 'Grupos de recursos' de um cliente: lista com contagem de recursos,
 * renomear inline, excluir com confirmação e form de criação.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import type { EquipmentGroup } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Badge, Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { equipmentGroupsOptions, errorMessage } from './resources-queries';
import {
  useCreateEquipmentGroup,
  useDeleteEquipmentGroup,
  useUpdateEquipmentGroup,
} from './resources-mutations';

export function ResourceGroupsDialog({
  clientId,
  clientName,
  onClose,
}: {
  clientId: number;
  clientName: string;
  onClose: () => void;
}) {
  const groupsQ = useQuery(equipmentGroupsOptions(clientId));
  const create = useCreateEquipmentGroup();
  const update = useUpdateEquipmentGroup();
  const del = useDeleteEquipmentGroup();

  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const [toDelete, setToDelete] = useState<EquipmentGroup | null>(null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate({ clientId, name: name.trim() }, { onSuccess: () => setName('') });
  };

  const saveRename = () => {
    if (!editing || !editing.name.trim()) return;
    update.mutate(
      { id: editing.id, body: { clientId, name: editing.name.trim() } },
      { onSuccess: () => setEditing(null) }
    );
  };

  const closeDelete = () => {
    setToDelete(null);
    del.reset();
  };

  return (
    <Dialog open onClose={onClose} title="Grupos de recursos" className="sm:max-w-lg">
      <p className="mb-3 text-[13px] text-muted-foreground">
        Grupos do cliente <span className="font-medium text-foreground">{clientName}</span>.
      </p>
      {groupsQ.isPending ? (
        <div className="space-y-2" aria-busy>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : groupsQ.isError ? (
        <p className="text-sm text-destructive">
          Erro ao carregar grupos: {errorMessage(groupsQ.error)}
        </p>
      ) : groupsQ.data.length === 0 ? (
        <EmptyState className="h-24" message="Nenhum grupo de recursos para este cliente" />
      ) : (
        <div className="rounded-md border">
          {groupsQ.data.map((g) => (
            <div
              key={g.id}
              className="flex h-11 items-center gap-2.5 border-b border-divider px-3 last:border-0"
            >
              {editing?.id === g.id ? (
                <>
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ id: g.id, name: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveRename();
                      }
                    }}
                    aria-label={`Novo nome do grupo ${g.name}`}
                    className="h-7 flex-1 text-sm"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    title="Salvar nome"
                    aria-label="Salvar nome do grupo"
                    disabled={update.isPending || !editing.name.trim()}
                    onClick={saveRename}
                  >
                    <Check />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    title="Cancelar"
                    aria-label="Cancelar renomeação"
                    onClick={() => setEditing(null)}
                  >
                    <X />
                  </Button>
                </>
              ) : (
                <>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium" title={g.name}>
                    {g.name}
                  </span>
                  <Badge>
                    {g.equipmentCount} recurso{g.equipmentCount === 1 ? '' : 's'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    title="Renomear grupo"
                    aria-label={`Renomear grupo ${g.name}`}
                    onClick={() => setEditing({ id: g.id, name: g.name })}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    title="Excluir grupo"
                    aria-label={`Excluir grupo ${g.name}`}
                    className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                    onClick={() => setToDelete(g)}
                  >
                    <Trash2 />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {update.error ? (
        <p className="mt-2 text-sm text-destructive">
          Erro ao renomear grupo: {errorMessage(update.error)}
        </p>
      ) : null}

      {/* Form inline: novo grupo */}
      <form onSubmit={submit} className="mt-4 border-t border-divider pt-4">
        <Label className="text-[13px]">Novo grupo</Label>
        <div className="mt-1.5 flex items-center gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome do grupo…"
            aria-label="Nome do grupo de recursos"
            className="h-8 flex-1 text-sm"
          />
          <Button type="submit" size="sm" disabled={create.isPending || !name.trim()}>
            <Plus />
            Adicionar
          </Button>
        </div>
        {create.error ? (
          <p className="mt-2 text-sm text-destructive">
            Erro ao criar grupo: {errorMessage(create.error)}
          </p>
        ) : null}
      </form>

      {toDelete ? (
        <Dialog open onClose={closeDelete} title="Excluir grupo">
          <p className="text-sm text-muted-foreground">
            Excluir o grupo <span className="font-medium text-foreground">{toDelete.name}</span>?
            {toDelete.equipmentCount > 0
              ? ` Os ${toDelete.equipmentCount} recursos do grupo ficarão sem grupo.`
              : ''}
          </p>
          {del.error ? (
            <p className="mt-2 text-sm text-destructive">{errorMessage(del.error)}</p>
          ) : null}
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={closeDelete}>
              Voltar
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={del.isPending}
              onClick={() => del.mutate(toDelete.id, { onSuccess: closeDelete })}
            >
              {del.isPending ? 'Excluindo…' : 'Excluir grupo'}
            </Button>
          </div>
        </Dialog>
      ) : null}
    </Dialog>
  );
}
